import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { api } from '../api'
import { Knopf } from '../components/Knopf'

/** Was `POST /api/auth/verify` zurückgibt (src/worker/routes/auth.ts). */
export interface AngemeldeterBenutzer {
  id: string
  email: string
  display_name: string
  is_platform_admin: boolean
}

/**
 * Die deutsche Nachricht eines Server-Fehlers, wörtlich. Bewusst entenmäßig statt
 * `instanceof ApiError`: dieser Teil bindet sich nicht an die Klassensignatur aus Part 23,
 * und ein gemockter Fehler im Komponententest ist ein gewöhnlicher Error.
 */
export function fehlerText(fehler: unknown): string {
  if (fehler instanceof Error && fehler.message !== '') return fehler.message
  return 'Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es später erneut.'
}

/** Das `field` aus `{error, message, field?}`, wenn der Server eines mitgeschickt hat. */
export function fehlerFeld(fehler: unknown): string | null {
  if (typeof fehler !== 'object' || fehler === null || !('field' in fehler)) return null
  const feld = (fehler as { field?: unknown }).field
  return typeof feld === 'string' && feld !== '' ? feld : null
}

export interface LoginSuche {
  /** Magic-Link aus der E-Mail: `<login_codes.id>.<sechsstelliger Code>` (Spec §1.11). */
  c?: string
  /** Ziel nach der Anmeldung; nur seiteninterne Pfade werden übernommen. */
  weiter?: string
}

/** Ids enthalten keinen Punkt, der Code sind sechs Ziffern — getrennt wird am letzten Punkt. */
export function parseMagicLink(c: string | undefined): { code_id: string; code: string } | null {
  if (c === undefined) return null
  const trenner = c.lastIndexOf('.')
  if (trenner <= 0) return null
  const code_id = c.slice(0, trenner)
  const code = c.slice(trenner + 1)
  if (code_id === '' || !/^\d{6}$/.test(code)) return null
  return { code_id, code }
}

/**
 * Offene Weiterleitung ausschließen: nur ein Pfad dieser Anwendung ist ein Ziel.
 *
 * Der Präfix-Vergleich allein genügt dafür nicht. Der URL-Parser der Browser behandelt bei einem
 * besonderen Schema den Rückwärtsstrich wie einen Schrägstrich und entfernt Tabulator und
 * Zeilenumbruch vor dem Parsen — `/\evil.test` und `/<TAB>/evil.test` beginnen beide mit genau
 * einem `/` und landen trotzdem auf einer fremden Herkunft. Deshalb entscheidet hier derselbe
 * Parser, der auch `window.location.assign` auswertet, und übernommen wird nur, was danach
 * nachweislich auf dieser Herkunft liegt.
 *
 * Die geprüfte Herkunft allein genügt am Ende ebenfalls nicht: der Parser entfernt ein führendes
 * `/..` und behält den folgenden doppelten Schrägstrich, `new URL('/..//evil.test', origin)` hat
 * die eigene Herkunft, aber den Pfad `//evil.test`. An `window.location.assign` weitergereicht
 * wäre das erneut eine schemarelative Adresse auf fremder Herkunft. Deshalb wird zuletzt das
 * zusammengesetzte Ergebnis selbst geprüft: genau ein führender Schrägstrich, sonst die Startseite.
 */
export function sicheresZiel(weiter: string | undefined): string {
  if (weiter === undefined || !weiter.startsWith('/')) return '/'
  // Steuerzeichen (der Parser wirft sie weg) und Rückwärtsstriche (er liest sie als Schrägstrich)
  // haben in einem seiteninternen Pfad nichts zu suchen.
  if (/[\u0000-\u001f\u007f\\]/.test(weiter)) return '/'
  try {
    const ziel = new URL(weiter, window.location.origin)
    if (ziel.origin !== window.location.origin) return '/'
    const pfad = ziel.pathname + ziel.search + ziel.hash
    if (!pfad.startsWith('/') || pfad.startsWith('//')) return '/'
    return pfad
  } catch {
    return '/'
  }
}

/**
 * Der Satz nach „Code anfordern". Er nennt die Adresse absichtlich nicht: `POST /api/auth/code`
 * antwortet für eine bekannte und eine unbekannte Adresse identisch, und dieser Text ist die
 * einzige Stelle, an der die Oberfläche den Unterschied verraten könnte.
 */
export const CODE_BESTAETIGUNG =
  'Wenn zu dieser Adresse ein Zugang besteht, haben wir Ihnen soeben einen sechsstelligen Code geschickt. '
  + 'Er gilt zehn Minuten. Die E-Mail enthält den Code und einen Link, der Sie direkt anmeldet.'

type Phase = 'adresse' | 'code' | 'magic'

export function LoginScreen({
  suche,
  onAngemeldet,
}: {
  suche: LoginSuche
  onAngemeldet: (benutzer: AngemeldeterBenutzer) => void
}): ReactElement {
  const magic = parseMagicLink(suche.c)
  const [phase, setPhase] = useState<Phase>(magic === null ? 'adresse' : 'magic')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [feld, setFeld] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const magicGelaufen = useRef(false)
  // Derselbe Zustand wie `laeuft`, nur sofort lesbar. `setLaeuft(true)` wirkt erst beim nächsten
  // Rendern; zwei Absendungen in derselben Runde — Enter neben dem Klick, ein zweites
  // Submit-Ereignis — kämen beide durch, und jeder überzählige `POST /api/auth/verify` verbraucht
  // einen der fünf Versuche, die der Server je Code zählt (src/worker/auth/codes.ts).
  const laeuftJetzt = useRef(false)

  useEffect(() => {
    if (magic === null || magicGelaufen.current) return
    magicGelaufen.current = true
    // Die Query-Zeile trägt den Code — sie wird sofort aus der Adresszeile und aus dem
    // Verlauf entfernt, bevor irgendetwas anderes passiert (Spec §1.11). Das geht bewusst an der
    // Router-Historie vorbei: der Screen kennt den Router nicht (er wird ohne ihn getestet), und
    // die Query-Zeile muss weg sein, bevor der erste `await` läuft — nicht erst, wenn eine
    // Navigation durchgereicht wurde. Gelingt die Anmeldung, ersetzt ohnehin ein voller
    // Ladevorgang die Seite; scheitert sie, bleibt der Nutzer hier, und die im Router noch
    // hinterlegte Suche `?c=…` kann bei einer späteren Navigation wieder in der Adresszeile
    // auftauchen — sichtbar, aber wertlos, denn der Code ist verbraucht.
    window.history.replaceState(null, '', window.location.pathname)
    void (async () => {
      laeuftJetzt.current = true
      setLaeuft(true)
      try {
        const antwort = await api.post<{ user: AngemeldeterBenutzer }>('/api/auth/verify', {
          code_id: magic.code_id,
          code: magic.code,
        })
        onAngemeldet(antwort.user)
      } catch (ausnahme) {
        setFehler(fehlerText(ausnahme))
        setPhase('adresse')
      } finally {
        laeuftJetzt.current = false
        setLaeuft(false)
      }
    })()
  }, [magic, onAngemeldet])

  async function codeAnfordern(ereignis: { preventDefault: () => void }): Promise<void> {
    ereignis.preventDefault()
    if (laeuftJetzt.current) return
    laeuftJetzt.current = true
    setFehler(null)
    setFeld(null)
    setLaeuft(true)
    try {
      await api.post('/api/auth/code', { email })
      setPhase('code')
    } catch (ausnahme) {
      setFehler(fehlerText(ausnahme))
      setFeld(fehlerFeld(ausnahme))
    } finally {
      laeuftJetzt.current = false
      setLaeuft(false)
    }
  }

  async function anmelden(ereignis: { preventDefault: () => void }): Promise<void> {
    ereignis.preventDefault()
    if (laeuftJetzt.current) return
    laeuftJetzt.current = true
    setFehler(null)
    setFeld(null)
    setLaeuft(true)
    try {
      const antwort = await api.post<{ user: AngemeldeterBenutzer }>('/api/auth/verify', { email, code })
      onAngemeldet(antwort.user)
    } catch (ausnahme) {
      setFehler(fehlerText(ausnahme))
      setFeld(fehlerFeld(ausnahme))
    } finally {
      laeuftJetzt.current = false
      setLaeuft(false)
    }
  }

  return (
    <div className="anmeldung">
      {/* Das Markenfeld: dieselbe Slate-Fläche und dieselbe Wortmarke wie im FinSuites-CRM. Reine
          Dekoration bis auf die Wortmarke — deren alt-Text nennt die Marke. */}
      <section className="anmeldung-marke" aria-label="FinSuites Accounting">
        <div className="anmeldung-logo">
          <img src="/brand/finsuites-wortmarke.png" alt="FinSuites" width={456} height={89} />
          <span className="leiste-produkt">Accounting</span>
        </div>
        <p className="anmeldung-satz">
          Provisionen, Abrechnungen und Belege — <span>an einem Ort, auf den Cent.</span>
        </p>
        <ul className="anmeldung-punkte" role="list">
          <li>Provisionen rechnen sich aus den Zahlungen in FinSuites</li>
          <li>Rechnung erstellen mit einem Klick</li>
          <li>Export für den Steuerberater</li>
        </ul>
      </section>

      <div className="anmeldung-karte">
      <h1>Anmelden</h1>
      <p className="mb-8 mt-2 text-[var(--ink-muted)]">Bitte melden Sie sich mit Ihrer E-Mail-Adresse an.</p>

      {fehler !== null && (
        <p role="alert" className="mb-6 border-l-2 border-[var(--alert)] pl-3 text-[var(--alert)]">
          {fehler}
        </p>
      )}

      {phase === 'magic' && (
        <p role="status">Sie werden angemeldet …</p>
      )}

      {phase === 'adresse' && (
        <form onSubmit={(ereignis) => void codeAnfordern(ereignis)} noValidate>
          <label htmlFor="login-email" className="block text-sm text-[var(--ink-muted)]">
            E-Mail-Adresse
          </label>
          <input
            id="login-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(ereignis) => setEmail(ereignis.target.value)}
            aria-invalid={feld === 'email' ? true : undefined}
            className="anmeldung-eingabe"
          />
          <Knopf type="submit" disabled={laeuft}>Code anfordern</Knopf>
        </form>
      )}

      {phase === 'code' && (
        <>
          <p role="status" className="mb-6">{CODE_BESTAETIGUNG}</p>
          <form onSubmit={(ereignis) => void anmelden(ereignis)} noValidate>
            <label htmlFor="login-code" className="block text-sm text-[var(--ink-muted)]">
              Sechsstelliger Code
            </label>
            <input
              id="login-code"
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              value={code}
              onChange={(ereignis) => setCode(ereignis.target.value.trim())}
              aria-invalid={feld === 'code' ? true : undefined}
              className="zahl anmeldung-eingabe anmeldung-code"
            />
            <Knopf type="submit" disabled={laeuft}>Anmelden</Knopf>
          </form>
          <p className="aktionen">
            {/* Derselbe Aufruf wie „Code anfordern“: ein neuer Code, der alte verfällt. */}
            <Knopf variante="still" disabled={laeuft} onClick={() => { void codeAnfordern({ preventDefault: () => undefined }) }}>
              Code erneut senden
            </Knopf>
            <Knopf
              variante="still"
              onClick={() => {
                setPhase('adresse')
                setCode('')
                setFehler(null)
                setFeld(null)
              }}
            >
              Andere Adresse verwenden
            </Knopf>
          </p>
          {/* Der Satz oben nennt mit Absicht nicht, ob es die Adresse gibt. Dieser hier sagt, woran es
              sonst liegt — sonst klickt jemand fünfmal auf „erneut senden“, dem schlicht die Einladung fehlt. */}
          <p className="hinweis">
            Keine E-Mail bekommen? Bitte auch im Spam-Ordner nachsehen. Einen Zugang gibt es erst nach einer
            Einladung: Auftragnehmer laden ihr Auftraggeber unter Einstellungen → Team ein, Auftraggeber der
            Plattform-Administrator. Der Link in der Einladung legt den Zugang an; danach funktioniert die Anmeldung hier.
          </p>
        </>
      )}
      </div>
    </div>
  )
}

function LoginRoute(): ReactElement {
  const suche = Route.useSearch()
  // Nach dem Anmelden wird die Seite vollständig neu geladen: das Session-Cookie ist gesetzt,
  // und ein voller Ladevorgang holt `GET /api/auth/me` frisch, statt einen Cache zu raten.
  return <LoginScreen suche={suche} onAngemeldet={() => { window.location.assign(sicheresZiel(suche.weiter)) }} />
}

export const Route = createFileRoute('/login')({
  validateSearch: (roh: Record<string, unknown>): LoginSuche => ({
    c: typeof roh['c'] === 'string' ? roh['c'] : undefined,
    weiter: typeof roh['weiter'] === 'string' ? roh['weiter'] : undefined,
  }),
  component: LoginRoute,
})
